// @flow

import * as React from 'react';
import Card from 'sail/Card';
import {useVideos} from 'docs/data';
import {debounce} from 'docs/util';

// This component exists to inform the actual `VideoPlayer` about where to position itself.
// The `VideoPlayer` component uses a fixed position outside out the shell's view hierarchy.
// This is done so the modal layer works properly and has the correct stacking context.
// https://git.corp.stripe.com/stripe-internal/pay-server/pull/351876
export const VideoPlayerPlaceholder = () => {
  const {setVideoPlayerPosition} = useVideos();

  const ref = React.useRef<?HTMLDivElement>();
  const [isMounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    const updatePlayerPosition = () => {
      const currentRef = ref.current;

      if (currentRef) {
        setVideoPlayerPosition(currentRef.getBoundingClientRect());
      }
    };

    // NOTE(jasons): Browsers are really finicky with how they send resize events,
    // especially when transitioning to full screen on macOS.
    // Having these two checks allows us to attempt to make sure we eventually
    // always show the correct position for the player.
    const quickFinishedResizing = debounce(updatePlayerPosition, 500);
    const slowFinishedResizing = debounce(updatePlayerPosition, 1000);

    const pageResized = () => {
      updatePlayerPosition();
      quickFinishedResizing();
      slowFinishedResizing();
    };

    const debouncedPageResized = debounce(pageResized, 5);

    window.addEventListener('resize', debouncedPageResized);

    document.addEventListener('fullscreenchange', pageResized);

    // fire once initially
    updatePlayerPosition();

    return () => {
      window.removeEventListener('resize', debouncedPageResized);
      document.removeEventListener('fullscreenchange', pageResized);
    };
  }, [setVideoPlayerPosition]);

  return (
    <Card
      shadow={isMounted ? 'none' : 'small'}
      boxRef={ref}
      margin={{vertical: 32}}
      style={{height: 'var(--video-player-mini-height)'}}
    />
  );
};
